import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTenantContext } from '../contexts/TenantContext';
import { ApiError } from '../lib/api';

// WS.C2 (#1588) — allowlist of tenant_members.role values that represent a
// real, full workspace membership. NOT a `role !== 'CLIENT'` denylist: WS.C2
// adjudication (#1615) found tenant_members.role isn't validated at every
// write path (POST /tenants/:id/members accepts an arbitrary string), so a
// denylist would render a garbage-role tenant as switchable. An allowlist
// fails closed against anything unrecognized instead. Mirrors the identical
// 3-value set already established in tenant-auth.middleware.ts's
// TENANT_ROLE_RANK and usage.ts's USAGE_ALLOWED_ROLES.
const SWITCHABLE_ROLES = new Set(['TENANT_ADMIN', 'TENANT_MEMBER', 'ADMIN']);

export default function TenantSwitcher() {
  const { currentTenant, availableTenants, switchTenant } = useTenantContext();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // R2 review — bumped on every close, so a switchTenant() rejection that
  // settles AFTER the popover has already been closed can't resurrect a
  // stale error into a later reopen. handleSelect() captures the value at
  // call time and checks it's still current before touching state.
  const requestIdRef = useRef(0);

  const closePopover = () => {
    requestIdRef.current += 1;
    setOpen(false);
    setError(null);
    // Deliberately NOT resetting `switching` here. If a request is still in
    // flight when the popover closes, leave the rows disabled (once
    // reopened) until it actually settles — otherwise a second click could
    // start a concurrent second request, and whichever resolves first wins
    // switchTenant()'s reload, which may not be the one the user most
    // recently clicked. `switching` always gets reset in handleSelect's
    // catch below, whether or not that settle turns out to be superseded.
  };

  const toggleOpen = () => {
    if (open) {
      closePopover();
    } else {
      setError(null);
      setOpen(true);
    }
  };

  // Outside-click close — mirrors NotificationBell.tsx's established pattern.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closePopover();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Escape-to-close + focus-return — borrowed from TierFeaturesPopover.tsx.
  // Arrow-key row navigation is deliberately out of scope for v1 (#1588 §4/Q4).
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePopover();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Render-nothing-until-known — matches the existing convention already
  // used for the role line below (roleLabel() returns null while unknown).
  if (!currentTenant) return null;

  const switchable = availableTenants.filter((t) => SWITCHABLE_ROLES.has(t.role));
  // Count destinations OTHER than where the user already is — NOT
  // `switchable.length > 1`. If currentTenant itself isn't in `switchable`
  // (e.g. it's CLIENT-role, reachable via WS.D1's recency-based default
  // landing — see open issue #1608), `switchable.length` can be exactly 1
  // while that entry is a perfectly valid destination. Counting "others"
  // directly is the only formula correct in both directions.
  const otherDestinations = switchable.filter((t) => t.id !== currentTenant.id).length;

  if (otherDestinations < 1) return null;

  const handleSelect = async (tenantId: string) => {
    // R1 review — re-selecting the current tenant is a no-op: no state
    // actually changes, so skip the round-trip + forced reload entirely.
    if (tenantId === currentTenant.id) return;
    // R2 review — synchronous re-entrancy guard. `disabled={switching}` alone
    // only takes effect after React's next render commit; two clicks fired
    // before that commit (e.g. a fast double-click) would otherwise both
    // read the pre-update `switching` closure and both start a request.
    if (switching) return;
    const requestId = ++requestIdRef.current;
    setError(null);
    setSwitching(true);
    try {
      await switchTenant(tenantId);
      // Unreachable on success — switchTenant() reloads the page itself.
    } catch (err) {
      // Always clear `switching` on settle — closePopover() deliberately
      // does not, so this is the only place a superseded request's "busy"
      // state gets released back to the UI.
      setSwitching(false);
      // But only surface the error if this is still the current attempt —
      // a close (or reopen) since this request started already invalidated
      // it; don't resurrect a stale failure into that state.
      if (requestIdRef.current !== requestId) return;
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong switching workspaces. Please try again.',
      );
    }
  };

  const currentLabel = currentTenant.slug ?? currentTenant.shortId;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="tenant-switcher-trigger"
        className="truncate"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          width: '100%',
          padding: 0,
          marginTop: 1,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'rgba(250,250,249,0.45)',
          fontFamily: 'var(--font-body)',
          fontSize: 12,
        }}
      >
        <span className="truncate" style={{ flex: 1, minWidth: 0 }}>
          {currentTenant.name} · {currentLabel}
        </span>
        <ChevronDown size={12} style={{ flexShrink: 0 }} />
      </button>

      {open && (
        <div
          data-testid="tenant-switcher-popover"
          style={{
            position: 'absolute',
            left: 0,
            bottom: '100%',
            marginBottom: 8,
            width: 260,
            maxHeight: 320,
            overflowY: 'auto',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.25))',
            zIndex: 1000,
          }}
        >
          {error && (
            <p
              role="alert"
              data-testid="tenant-switcher-error"
              style={{
                color: 'var(--error)',
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                padding: '10px 16px 0',
                margin: 0,
              }}
            >
              {error}
            </p>
          )}
          {switchable.map((t) => {
            const isCurrent = t.id === currentTenant.id;
            return (
            <button
              key={t.id}
              type="button"
              onClick={() => void handleSelect(t.id)}
              disabled={switching || isCurrent}
              data-testid={`tenant-switcher-option-${t.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'left',
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                cursor: switching || isCurrent ? 'default' : 'pointer',
                opacity: t.isPersonal ? 0.6 : 1,
              }}
            >
              <span style={{ width: 14, flexShrink: 0, display: 'flex' }}>
                {isCurrent && (
                  <Check
                    size={14}
                    data-testid="tenant-switcher-checkmark"
                    style={{ color: 'var(--primary-light, var(--primary))' }}
                  />
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  className="truncate"
                  style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}
                >
                  {t.name}
                </span>
                <span
                  className="truncate"
                  style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}
                >
                  {t.slug ?? t.shortId}
                  {t.viaPartner && (
                    <span data-testid={`tenant-switcher-partner-${t.id}`} style={{ marginLeft: 6, color: 'var(--primary-light, var(--primary))' }}>
                      · Partner access
                    </span>
                  )}
                </span>
              </span>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
